import { apiEndpoint } from "../utils/env";

/**
 * Pointing a colleague at a document.
 *
 * <b>A share is a pointer, never a grant.</b> Nothing here widens what its recipient may read: they pass
 * the same organisation gate and access-grant matrix as they would reaching the document any other way,
 * resolved from their own token. That is why the inbox marks rows the reader may not open rather than
 * hiding or unlocking them — an honest "somebody sent you this, and you are not entitled to it" is the
 * correct outcome, not a bug to design away.
 *
 * External sharing is a different feature entirely: the delivery portal, with recipients, tokens and a
 * verification question. This is only for colleagues who already have logins.
 */
async function call(path, token, options = {}) {
  const headers = { ...(options.headers || {}) };
  if (token) headers["Authorization"] = `Bearer ${token}`;
  if (options.body) headers["Content-Type"] = "application/json";

  const res = await fetch(apiEndpoint(path), { ...options, headers });
  const txt = await res.text();
  let json = null;
  try { json = txt ? JSON.parse(txt) : null; } catch { json = txt; }

  if (!res.ok) {
    const msg = (json && json.message) || (typeof json === "string" ? json : res.statusText);
    const err = new Error(msg || `Request failed: ${res.status}`);
    err.status = res.status;
    err.code = json && json.code;
    err.details = json;
    throw err;
  }
  return json;
}

const q = encodeURIComponent;

/**
 * Each result says what happened to one recipient: `status` shared|alreadyShared, `notified` (false when
 * no account by that name could be found — the share is still recorded), and `recipientCanOpen` (false
 * when the grant matrix will refuse them the document, so the sender learns it now rather than later).
 */
export function shareDocument(token, { databaseName, archiveNo, recipients, message, expiresAt }) {
  return call("/api/Document/Shares", token, {
    method: "POST",
    body: JSON.stringify({ databaseName, archiveNo, recipients, message, expiresAt }),
  });
}

export function listShares(token, databaseName, archiveNo) {
  return call(`/api/Document/Shares?databaseName=${q(databaseName)}&archiveNo=${q(archiveNo)}`, token);
}

/** Withdraw. A tombstone: the row survives so an access review can still answer who was pointed at what. */
export function revokeShare(token, id, databaseName) {
  return call(`/api/Document/Shares/${id}?databaseName=${q(databaseName)}`, token, { method: "DELETE" });
}

/** The caller's own past recipients — no directory lookup, so no staff-list disclosure. */
export function recentRecipients(token) {
  return call("/api/Document/Shares/Recent", token);
}

export function sharedWithMe(token, { includeRead = true } = {}) {
  return call(`/api/Document/SharedWithMe?includeRead=${includeRead ? "true" : "false"}`, token);
}

export function markShareRead(token, id) {
  return call(`/api/Document/SharedWithMe/${id}/Read`, token, { method: "POST" });
}
